import { memo, useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { Zap } from 'lucide-react';
import { boostBasePenaltyMs, resolveTimeControl } from '@draughts/shared';
import { useTranslation } from 'react-i18next';
import { useArenaStore } from '../../store/arena-store';
import { useArenaPartita } from './useArenaPartita';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { haMosso, mosseGiocate } from '../../utils/ha-mosso';
import { log } from '../../utils/logger';
import { vibrate } from '../../utils/vibration';
import './ArenaBoostButton.css';

/** Quanto resta a schermo il messaggio del bottone (fatto / non riuscito). */
const BOOST_MESSAGE_MS = 2200;

/**
 * Un orologio in `m:ss`.
 *
 * Locale e non condiviso perche' un formattatore d'orologio comune non esiste
 * (la barra, l'arena e le sfide ne hanno ognuna una riga propria) e inventarlo
 * qui sarebbe un secondo lavoro dentro il primo. Qui i valori sono minuti interi
 * e mezzi minuti (`7:00`, `3:30`): il dimezzamento e' `floor(base / 2)`.
 */
function formattaTempo(ms: number): string {
  const secondiTotali = Math.round(ms / 1000);
  const minuti = Math.floor(secondiTotali / 60);
  const secondi = secondiTotali % 60;
  return `${minuti}:${String(secondi).padStart(2, '0')}`;
}

/**
 * C'è ancora qualcosa da dire nel posto di Focus?
 *
 * Sta fuori dal componente perché la risposta non serve solo a lui: il bottone
 * vive **al posto di Focus** dentro la barra dei comandi, e chi monta la barra
 * (`ArenaGame`) deve sapere se passare il boost o lasciare Focus. Se lo tenesse
 * per sé restituendo `null`, quel posto resterebbe vuoto.
 *
 * Non è solo "si può ancora boostare": comprende anche il momento in cui il
 * boost è appena stato preso o è appena fallito, perché sparire di colpo era
 * il modo in cui l'esito non si vedeva.
 */
export function useCanBoost(): boolean {
  const boostPhase = useArenaStore((state) => state.boostPhase);
  /*
   * Le mosse GIOCATE, non quelle sulla scacchiera: `openingPlyCount` sono le
   * semi-mosse che il sorteggio d'apertura mette prima che i due giocatori
   * muovano, e contarle chiuderebbe la finestra prima ancora che si apra —
   * il bottone non comparirebbe mai. È lo stesso conto che il server fa con
   * `GameRoom.getPlayedMoveCount()`.
   */
  const plies = useArenaStore((state) => state.boardState?.moveHistory?.length ?? 0);
  const openingPlyCount = useArenaStore((state) => state.openingPlyCount);
  const moveCount = mosseGiocate(plies, openingPlyCount);
  const turnoCorrente = useArenaStore((state) => state.boardState?.currentTurn ?? null);
  const playerColor = useArenaStore((state) => state.playerColor);
  const status = useArenaStore((state) => state.status);
  /*
   * E prima di tutto: in quest'arena il boost si usa? (migration 118)
   *
   * `useArenaPartita` e non `currentArena`: l'arena di cui leggere le regole e'
   * quella in cui si STA GIOCANDO, che non e' sempre quella aperta a schermo —
   * il saggio in testa a quel gancio racconta il caso. Qui sbagliarla vorrebbe
   * dire spegnere il bottone a chi gioca in un'arena col boost solo perche' ne
   * ha aperta un'altra dall'elenco.
   *
   * `=== false` e non `!...`: il campo e' facoltativo, e un Node vecchio che
   * non lo manda descrive un'arena col boost, non un'arena senza.
   */
  const arenaPartita = useArenaPartita();

  if (arenaPartita?.boostAllowed === false) return false;
  if (status !== 'playing' || playerColor === null) return false;
  /*
   * Il "fatto" si mostra dove si era premuto, e solo per un momento — ma NON
   * oltre la finestra. Chi boostava e muoveva entro i 2,2 secondi del messaggio
   * si trovava il bottone ancora vivo e cliccabile: il tocco partiva, il server
   * rispondeva TOO_LATE, il bottone spariva e il boost restava acceso su un
   * utente che aveva appena chiesto di spegnerlo.
   */
  if (boostPhase === 'confirmed' && !haMosso(playerColor, moveCount, turnoCorrente)) return true;
  /*
   * `closed` e' l'unico stato che chiude davvero: lo scrive il `TOO_LATE` del
   * server. `boostActivated` NON chiude piu' niente -- il boost e' un
   * interruttore, e sparire appena acceso vorrebbe dire togliere il solo modo
   * di spegnerlo.
   */
  if (boostPhase === 'closed') return false;

  return !haMosso(playerColor, moveCount, turnoCorrente);
}

/**
 * Che cosa costa il boost, coi numeri di QUESTA cadenza.
 *
 * La regola non si riscrive qui: `boostBasePenaltyMs` e' la stessa funzione che
 * il server applica in `GameRoom.applyBoost`. Il dimezzamento non ha casi — su
 * `rapid-7+5` il tempo passa da 7:00 a 3:30 e sparisce il +5, su `blitz-2+5` da
 * 2:00 a 1:00 e sparisce il +5 — quindi a cambiare la frase resta una cosa sola:
 * se quella cadenza un incremento ce l'abbia o no. Dire «perdi l'incremento» su
 * `blitz-5+0` nominerebbe un prezzo che nessuno paga.
 *
 * Senza cadenza leggibile (`null`, `'none'`, o una chiave che
 * `resolveTimeControl` non sa leggere) si torna alla frase generica del pannello
 * delle regole: vaga, ma vera ovunque.
 */
function descriviCosto(t: TFunction, cadenza: string | null): string {
  const config = cadenza && cadenza !== 'none' ? resolveTimeControl(cadenza) : null;
  if (!config || config.initialTime <= 0) return t('arena.info.boostTimeCost');

  const penalita = boostBasePenaltyMs(config.initialTime);
  const prima = formattaTempo(config.initialTime);
  const dopo = formattaTempo(config.initialTime - penalita);
  const incremento = Math.round(config.increment / 1000);

  // Senza incremento non c'e' niente da togliere li': resta solo l'orologio
  // da nominare.
  if (incremento === 0) return t('arena.confirmBoost.halveOnly', { prima, dopo });

  return t('arena.confirmBoost.halve', { prima, dopo, incremento });
}

/**
 * ArenaBoostButton - paga tempo, raddoppia i punti della vittoria.
 *
 * Il prezzo è l'orologio da tutt'e due i lati: il boost toglie **sempre**
 * l'incremento e dimezza **sempre** il tempo base, su qualunque cadenza
 * (`boostBasePenaltyMs`, `shared/src/types/arena.ts`). Su `blitz-2+5` l'orologio
 * passa da 2:00 a 1:00 e sparisce anche il +5 a mossa. La frase che il giocatore
 * legge sul `title` di questo bottone è `arena.info.boostTimeCost`, ed è la stessa
 * del pannello delle regole: se la regola cambia, cambiano tutt'e due o nessuna.
 *
 * È un interruttore, non una scelta irreversibile: si accende e si spegne
 * quante volte si vuole, e a chiudere la finestra è **la propria prima mossa**
 * — non il primo tocco. Spegnendolo il server rende esattamente la penalità
 * che aveva tolto, e l'incremento torna da sé. È anche quello che il messaggio
 * d'errore dice da sempre, in tutte e sette le lingue: «il boost si *cambia*
 * solo prima della tua prima mossa».
 *
 * Le due direzioni non si somigliano, ed è voluto. **Accendere chiede conferma**:
 * il tocco paga l'orologio all'istante, e il prezzo viveva solo nel `title` —
 * cioè in nessun posto, su un telefono. La conferma lo dice coi numeri della
 * cadenza in corso (`descriviCosto`) prima che parta qualunque cosa.
 * **Spegnere no**: l'etichetta del bottone acceso è già l'azione («Rimuovi», non
 * «Attivo»), il server rende tutto, e una seconda domanda metterebbe un ostacolo
 * proprio sulla via d'uscita.
 *
 * Il bottone racconta l'esito invece di sparire e basta: in attesa mentre il
 * server non ha risposto, "fatto" per un paio di secondi quando è preso,
 * "riprova" quando non è riuscito ma il momento c'è ancora. Del boost preso
 * resta poi il segno accanto al proprio nome per tutta la partita
 * (`ArenaBoostBadge`).
 */
const ArenaBoostButton = memo(function ArenaBoostButton() {
  const { t } = useTranslation();
  const canBoost = useCanBoost();
  const boostPhase = useArenaStore((state) => state.boostPhase);
  const boostActivated = useArenaStore((state) => state.boostActivated);
  const activateBoost = useArenaStore((state) => state.activateBoost);
  const deactivateBoost = useArenaStore((state) => state.deactivateBoost);
  const settleBoostPhase = useArenaStore((state) => state.settleBoostPhase);
  const timeControl = useArenaStore((state) => state.timeControl);
  const [chiedeConferma, setChiedeConferma] = useState(false);

  // Debug log only on state changes, not every render
  useEffect(() => {
    log.info('ArenaBoostButton', 'State changed', { canBoost, boostPhase });
  }, [canBoost, boostPhase]);

  // Il messaggio non resta lì per sempre: letto, il bottone torna al suo
  // mestiere (o lascia il posto a Focus).
  useEffect(() => {
    if (boostPhase !== 'confirmed' && boostPhase !== 'failed') return;
    const timer = setTimeout(settleBoostPhase, BOOST_MESSAGE_MS);
    return () => clearTimeout(timer);
  }, [boostPhase, settleBoostPhase]);

  const isPending = boostPhase === 'pending';
  const isFailed = boostPhase === 'failed';

  /*
   * Un tocco solo, due direzioni. `isConfirmed` non è più una condizione di
   * uscita: è il momento in cui il bottone dice "fatto", e da lì si deve poter
   * tornare indietro subito — chi si accorge dell'orologio dimezzato ritocca
   * nello stesso punto, e lì deve trovare l'interruttore che risponde.
   */
  const handleClick = useCallback(() => {
    if (!canBoost || isPending) return;

    // Haptic feedback
    vibrate(50);

    if (boostActivated) {
      log.info('ArenaBoostButton', 'Unboost requested');
      deactivateBoost();
      return;
    }
    // Accendere passa dalla conferma: qui non parte ancora niente per il server.
    setChiedeConferma(true);
  }, [canBoost, isPending, boostActivated, deactivateBoost]);

  const confermaBoost = useCallback(() => {
    setChiedeConferma(false);
    log.info('ArenaBoostButton', 'Boost requested');
    activateBoost();
  }, [activateBoost]);

  /* Chi ci monta sopra passa già solo quando serve (`useCanBoost`); questa è
     la rete, non la regola.

     Vale anche per la domanda aperta: la finestra può chiudersi mentre si
     legge la conferma (ci si è mossi, o è arrivato un `TOO_LATE`), e da qui se
     ne va anche lei — il portal sposta il DOM, non la proprietà: la conferma
     resta nell'albero di questo componente e si smonta con lui. Meglio così
     che lasciare a schermo un «Attiva boost» che il server rifiuterebbe. */
  if (!canBoost) return null;

  /*
   * Acceso, l'etichetta dice **cosa fa il prossimo tocco**, non dove siamo:
   * «Rimuovi». «Attivo» (`arena.game.boostDone`, tolto da tutte e otto le
   * lingue perché non lo invocava più nessuno) descriveva uno stato che la
   * veste ambra e il ⚡ accanto al nome già dicevano, e in cambio nascondeva
   * l'unica via d'uscita: chi si accorgeva dell'orologio dimezzato trovava lì
   * la conferma di ciò che voleva annullare, non il modo di annullarlo.
   */
  const label = isFailed
    ? t('arena.game.boostRetry')
    : boostActivated
      ? t('arena.game.boostRemove')
      : t('arena.game.boost');

  /*
   * Le classi della barra, non un riquadro tutto suo: il boost occupa il posto
   * di Focus e deve pesare quanto gli altri (stessa altezza, stesso divisorio,
   * icona sopra ed etichetta sotto). Solo il colore è suo.
   *
   * `aria-live` sull'etichetta: chi non guarda lo schermo l'esito lo deve
   * sentire, ed è l'unico posto in cui viene detto.
   */
  return (
    <>
      <button
        className={[
          'game-toolbar__btn',
          'game-toolbar__btn--boost',
          isPending && 'game-toolbar__btn--boost-pending',
          /* La veste «attivo» segue il BOOST, non la fase: dopo uno spegnimento
             riuscito `boostPhase` vale 'confirmed' con `boostActivated` falso, e
             il bottone portava per due secondi la veste accesa sotto l'etichetta
             «Boost». Le due conferme sono opposte e non devono somigliarsi. */
          boostActivated && 'game-toolbar__btn--boost-done',
          isFailed && 'game-toolbar__btn--boost-failed',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="arena-boost-button"
        data-boost-phase={boostPhase}
        data-boost-attivo={boostActivated ? 'si' : 'no'}
        onClick={handleClick}
        /* Solo mentre la richiesta è in volo: spegnerlo su `isConfirmed`
           toglierebbe il modo di cambiare idea nei due secondi del messaggio. */
        disabled={isPending}
        type="button"
        title={t('arena.info.boostTimeCost')}
        aria-label={label}
        /* È un interruttore, e chi non guarda lo schermo deve saperlo. */
        aria-pressed={boostActivated}
      >
        <span className="arena-boost-btn-icon" data-testid="arena-boost-icon" aria-hidden="true"><Zap size={20} /></span>
        <span aria-live="polite">{label}</span>
      </button>
      {chiedeConferma && (
        <ConfirmDialog
          title={t('arena.confirmBoost.title')}
          message={
            <>
              <p style={{ margin: '0 0 var(--spacing-sm) 0' }}>{descriviCosto(t, timeControl)}</p>
              <p style={{ margin: 0 }}>{t('arena.confirmBoost.reward')}</p>
            </>
          }
          confirmLabel={t('arena.confirmBoost.confirm')}
          confirmTestId="arena-boost-confirm"
          cancelTestId="arena-boost-cancel"
          /* Ambra e non rosso: il boost si spegne fino alla propria prima mossa,
             e il rosso è la parola del ritiro. */
          confirmTone="accent"
          onConfirm={confermaBoost}
          onCancel={() => setChiedeConferma(false)}
        />
      )}
    </>
  );
});

/**
 * Il boost preso, accanto al proprio nome, per tutto il resto della partita.
 *
 * Il bottone dice "fatto" e se ne va: due secondi dopo, di una scelta che vale
 * fino all'ultima mossa (l'incremento perso, i punti raddoppiati) non resterebbe
 * traccia da nessuna parte nella schermata di gioco. Lo stesso ⚡ della
 * classifica, nello stesso posto: dopo il nome.
 */
export const ArenaBoostBadge = memo(function ArenaBoostBadge() {
  const { t } = useTranslation();
  return (
    <span
      className="arena-boost-badge"
      data-testid="arena-boost-badge"
      role="img"
      aria-label={t('arena.standings.boostOn')}
      title={t('arena.info.boostTimeCost')}
    >
      <Zap size={14} aria-hidden="true" />
    </span>
  );
});

export default ArenaBoostButton;
